class PredictiveSearch extends HTMLElement {
  constructor() {
    super();

    this.cachedMap = new Map();
    this.focusElement = this.input;

    this.resetButton.addEventListener('click', this.clear.bind(this));
    this.input.addEventListener(
      'input',
      FoxTheme.utils.debounce(this.onChange.bind(this), 300)
    );
    this.input.addEventListener('focus', this.onFocus.bind(this));

    this.searchContent = this.querySelector('.search__content');
    this.searchRecommendationEmpty = this.dataset.searchRecommendationEmpty === 'true';
    this.header = document.querySelector('header');
    this.toggleNavigationButton = document.querySelector('.toggle-navigation-button');

    this.isHeaderSearch = this.closest('header') !== null;

    this.boundHandleClickOutside = this.handleClickOutside.bind(this);
    document.addEventListener('click', this.boundHandleClickOutside);

    this.searchProductTypes?.addEventListener(
      'change',
      this.handleProductTypeChange.bind(this)
    );

    document.addEventListener('menu-drawer:open', () => {
      this.toggleSearchState(false);
    });

    // 🔥 OPEN button
    document.querySelector('.header__search-open')?.addEventListener('click', (e) => {
      e.preventDefault();
      this.toggleSearchState(true);
      this.input?.focus();
    });

    document.querySelector('.header__search-open-mobile')?.addEventListener('click', (e) => {
      e.preventDefault();
      this.toggleSearchState(true);
      this.input?.focus();
    });

    this.states = {
      OPEN: 'predictive-search-open',
      LOADING: 'btn--loading',
      SEARCH_OPEN: 'search-open',
    };
  }

  /* =====================
     GETTERS
  ===================== */
  get input() {
    return this.querySelector('input[type="search"]');
  }

  get resetButton() {
    return this.querySelector('button[type="reset"]');
  }

  get searchProductTypes() {
    return this.querySelector('#SearchProductTypes');
  }

  /* =====================
     CORE
  ===================== */
  onFocus(event) {
    document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
    document.documentElement.style.setProperty(
      '--header-bottom-position',
      `${parseInt(this.header.getBoundingClientRect().bottom)}px`
    );

    if (this.isHeaderSearch && !this.searchRecommendationEmpty) {
      document.body.classList.add(this.states.SEARCH_OPEN);
      this.toggleNavButtonVisibility(false);
      this.toggleHeaderSearchClass(true);
    }

    if (!this.searchRecommendationEmpty) {
      this.searchContent?.classList.remove('hidden');
    }

    this.classList.add(this.states.OPEN);

    if (!this.getQuery()) return;

    this.renderSection(this.setupURL().toString(), event);
  }

  getQuery() {
    return this.input.value.trim();
  }

  clear(event = null) {
    event?.preventDefault();
    this.input.value = '';
    this.input.focus();
    this.removeAttribute('results');
    this.searchContent?.classList.add('hidden');
  }

  handleProductTypeChange() {
    if (!this.getQuery()) return;
    this.renderSection(this.setupURL().toString());
  }

  /* =====================
     URL + FETCH
  ===================== */
  setupURL() {
    const url = new URL(`${window.shopUrl}${FoxTheme.routes.predictive_search_url}`);
    let searchTerm = this.getQuery();

    if (this.searchProductTypes?.value) {
      searchTerm = `product_type:${this.searchProductTypes.value} AND ${encodeURIComponent(searchTerm)}`;
    }

    url.searchParams.set('q', searchTerm);
    url.searchParams.set('resources[limit]', this.dataset.resultsLimit || 3);
    url.searchParams.set('resources[limit_scope]', 'each');
    url.searchParams.set('section_id', FoxTheme.utils.getSectionId(this));

    return url;
  }

  onChange() {
    if (this.getQuery().length === 0) {
      this.searchContent?.classList.add('hidden');
      this.removeAttribute('results');
      return;
    }
  
    this.toggleSearchState(true);
    this.renderSection(this.setupURL().toString());
  }

  renderSection(url) {
    this.cachedMap.has(url)
      ? this.renderSectionFromCache(url)
      : this.renderSectionFromFetch(url);
  }

  renderSectionFromCache(url) {
    this.renderSearchResults(this.cachedMap.get(url));
    this.setAttribute('results', '');
  }

  renderSectionFromFetch(url) {
    this.setLoadingState(true);

    fetch(url)
      .then((r) => r.text())
      .then((html) => {
        this.cachedMap.set(url, html);
        this.renderSearchResults(html);
      })
      .catch(console.error)
      .finally(() => this.setLoadingState(false));
  }

  renderSearchResults(html) {
    const id = `PredictiveSearchResults-${FoxTheme.utils.getSectionId(this)}`;
    const target = document.getElementById(id);
    if (!target) return;

    const doc = new DOMParser().parseFromString(html, 'text/html');
    const content = doc.getElementById(id);
    if (!content) return;

    target.innerHTML = content.innerHTML;
    this.searchContent?.classList.remove('hidden');
    this.toggleSearchState(true);
  }

  /* =====================
     STATE / UI
  ===================== */
  handleClickOutside(event) {
    const target = event.target;

    const shouldClose = this.isHeaderSearch &&
      !this.contains(target) &&
      (
        target.classList.contains('header__search-close') ||
        (target.classList.contains('fixed-overlay') &&
         target.closest('.header-section'))
      );

    if (shouldClose) {
      setTimeout(() => this.toggleSearchState(false));
    }
  }

  toggleSearchState(isOpen) {
    this.classList.toggle(this.states.OPEN, isOpen);

    if (isOpen) {
      this.setAttribute('results', '');
    }

    if (this.isHeaderSearch) {
      document.body.classList.toggle(this.states.SEARCH_OPEN, isOpen);
      this.toggleNavButtonVisibility(!isOpen);
      this.toggleHeaderSearchClass(isOpen);
    }

    if (!isOpen && this.searchRecommendationEmpty) {
      this.searchContent?.classList.add('hidden');
    }
  }

  toggleHeaderSearchClass(isOpen) {
    const headerSearch = document.querySelector('.header__search');
    if (!headerSearch) return;
    headerSearch.classList.toggle('header_search-opened', isOpen);
  }

  toggleNavButtonVisibility(isOpen) {
    // optional
  }

  setLoadingState(isLoading) {
    this.resetButton.classList.toggle(this.states.LOADING, isLoading);
    isLoading ? this.setAttribute('loading', 'true') : this.removeAttribute('loading');
  }
}

customElements.define('predictive-search', PredictiveSearch);
